import { useState, type CSSProperties } from 'react';
import BSideListing from './BSideListing';
import OneStepAheadDetails from './OneStepAheadDetails';
import ToolAccessLabel from './ToolAccessLabel';
import { PREVIEW_INTRO, PROJECT_180 } from '../../core/preview';
import { PREVIEW_CATALOG, PREVIEW_NEEDS, type PreviewNeed } from '../../core/catalog';
import type { Listing } from '../../core/layouts';
import { LISTING_ICONS, ListingStaffBadge, STATUS_LABELS, listingsFromProps } from './layoutListings';
import type { PreviewLayoutProps } from './types';
import './labBench.css';

const TILT = [-2.2, 1.6, -1, 2.4, -1.8, 1.2, -2.6, 1.9];
const CARD_TINTS = ['mint', 'paper', 'lilac', 'paper', 'sky', 'paper', 'rose', 'sun'];
const STICKER_TINTS = ['sun', 'sky', 'lilac', 'mint', 'rose'];

/** E10 arm `lab_bench`: the catalogue as cards and stickers on a bench. */
export default function PreviewLabBench(props: PreviewLayoutProps) {
  const { onOsaLearnMore, programActions, marketplaceActions, updatesAction, bSideListing = null, onToolOpen, frontDoor = null } = props;
  const listings = listingsFromProps(props);
  // Project 180 sits on the bench as the first card, the same size as the tools.
  const project = PREVIEW_CATALOG.find(entry => entry.id === PROJECT_180.id);
  const bench: Listing[] = [
    ...(project ? [{ entry: project, status: 'coming_soon', period: 'before_bell' } as const] : []),
    ...listings.filter(l => l.status !== 'idea'),
  ];
  const ideas = listings.filter(l => l.status === 'idea');
  const [need, setNeed] = useState<PreviewNeed>('all');
  const shown = bench.filter(l => need === 'all' || l.entry.needs.includes(need as Exclude<PreviewNeed, 'all'>));
  const needs = PREVIEW_NEEDS.map(n => ({ ...n, count: n.id === 'all' ? bench.length : bench.filter(l => l.entry.needs.includes(n.id as Exclude<PreviewNeed, 'all'>)).length }))
    .filter(n => n.id === 'all' || n.count > 0);
  const stickers = bench.filter(l => l.status === 'open').slice(0, 5);
  const [before, after] = PREVIEW_INTRO.headline.split('real work');

  return (
    <main className="preview-page preview-lab-bench">
      {/* E13's question replaces the hero's headline; the updates card stays beside it. */}
      {frontDoor ? frontDoor(updatesAction && <div className="lb-updates">{updatesAction}</div>) : (
        <header className="lb-hero">
          <div>
            <span className="lb-eyebrow">{PREVIEW_INTRO.eyebrow}</span>
            <h1>{after === undefined ? PREVIEW_INTRO.headline : <>{before}<span className="lb-mark">real work<svg viewBox="0 0 300 30" preserveAspectRatio="none" aria-hidden="true"><path d="M4 22 C 60 6, 120 28, 180 14 S 270 8, 296 18" /></svg></span>{after}</>}</h1>
            <p className="lb-lede">{PREVIEW_INTRO.body}</p>
            {bench.length > 0 && <a className="lb-btn lb-hot" href="#lab-bench">See what’s on the bench →</a>}
          </div>
          <div className="lb-hero-side">
            {/* The Labs updates signup: a small card in the hero's top-right corner. */}
            {updatesAction && <div className="lb-updates">{updatesAction}</div>}
          <div className="lb-board" aria-hidden="true">
            <div className="lb-sticker lb-sticker-feature">{PROJECT_180.name}<small>{PROJECT_180.tagline}</small></div>
            {stickers.map((l, i) => (
              <div className={`lb-sticker lb-sticker-${i} lb-tint-${STICKER_TINTS[i % STICKER_TINTS.length]}`} key={l.entry.id}>
                {LISTING_ICONS[l.entry.id] ?? '✎'} {l.entry.name}
              </div>
            ))}
          </div>
          </div>
        </header>
      )}

      {bench.length > 0 && (
        <section className="lb-section" id="lab-bench" aria-labelledby="lab-bench-heading">
          <h2 className="lb-h2" id="lab-bench-heading">On the bench.</h2>
          <p className="lb-note">pick what you need today</p>
          {/* Filters only earn their place once there is a bench worth narrowing, and the front door asks the same thing. */}
          {!frontDoor && bench.length >= 4 && needs.length > 2 && (
            <div className="lb-filters" role="group" aria-label="Filter by need">
              {needs.map(n => (
                <button type="button" key={n.id} className="lb-chip" aria-pressed={need === n.id} onClick={() => setNeed(n.id)}>
                  {n.label}<span className="lb-count">{n.count}</span>
                </button>
              ))}
            </div>
          )}
          <div className="lb-cards">
            {shown.map((listing, i) => {
              const { entry, status, href } = listing;
              return (
                <article key={entry.id} className={`lb-card lb-tint-${CARD_TINTS[i % CARD_TINTS.length]}`} style={{ '--lb-r': `${TILT[i % TILT.length]}deg` } as CSSProperties} aria-labelledby={`${entry.id}-heading`}>
                  <span className={`lb-status lb-status-${status}`}>{STATUS_LABELS[status]}</span>
                  <span className="lb-emoji" aria-hidden="true">{LISTING_ICONS[entry.id] ?? '✎'}</span>
                  <h3 id={`${entry.id}-heading`}>{entry.name}</h3>
                  {status === 'open' && entry.destination && <ToolAccessLabel destination={entry.destination} />}
                  <ListingStaffBadge id={entry.id} props={props} />
                  <p>{entry.description}</p>
                  {entry.id === 'one_step_ahead' && <OneStepAheadDetails onOpen={onOsaLearnMore} />}
                  {href && <a className="lb-open" href={href} onClick={() => onToolOpen(entry.id)}>Open {entry.name} <span aria-hidden="true">→</span></a>}
                  {status === 'planned' && marketplaceActions && <div className="lb-actions">{marketplaceActions}</div>}
                  {status === 'coming_soon' && <div className="lb-actions lb-actions-stacked">{programActions}</div>}
                </article>
              );
            })}
          </div>
        </section>
      )}

      {ideas.length > 0 && (
        <section className="lb-ideas" aria-labelledby="lb-ideas-heading">
          <h2 id="lb-ideas-heading" className="lb-hand">Still on the whiteboard…</h2>
          <p>Ideas with a name and nothing behind them yet.</p>
          <div className="lb-idea-row">
            {ideas.map(({ entry }) => (
              <article className="lb-idea" key={entry.id} aria-labelledby={`${entry.id}-heading`}>
                <h3 id={`${entry.id}-heading`}><span aria-hidden="true">{LISTING_ICONS[entry.id] ?? '✎'} </span>{entry.name}</h3>
                <ListingStaffBadge id={entry.id} props={props} />
                <p>{entry.description}</p>
              </article>
            ))}
          </div>
        </section>
      )}

      <BSideListing listing={bSideListing} onOpen={() => onToolOpen('b_side')} />

      <p className="preview-footnote">
        240 Labs features may change or disappear. Sign in to your 240 account to get notified and save your choices across devices.
      </p>
    </main>
  );
}
